import styled from '@emotion/styled';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { TrashIcon } from 'src/domains/layouts/shared/assets/icons/TrashIcon';
import { WarningIcon } from 'src/domains/layouts/shared/assets/icons/WarningIcon';
import { RaceIndicator } from 'src/domains/sportsbook/webview/components/raceIndicator/RaceIndicator';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType, InputVariantType } from 'src/domains/layouts/config/features/types';
import { css, SerializedStyles } from '@emotion/react';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';

interface BetslipSelectionPropsType {
    variant: DefaultComponentVariantType | null;
}

interface BetslipEachWayBoxPropsType {
    variant: InputVariantType | null;
}

const setBetslipError = (variant: DefaultComponentVariantType| null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.xSmall};
        `;
    }
    return css``;
};

export const BetSlipError = withConfig(theme => styled('div', { label: 'BetSlipError' })<BetslipSelectionPropsType>`
    ${({ variant }): SerializedStyles => setBetslipError(variant, theme)}
`);

export const ReturnWrapper = withConfig(theme => styled('div', { label: 'ReturnWrapper' })`
    align-items: center;
    color: ${theme.star.betslipGeneral.txtColor};
    display: flex;
    font-size: ${theme.star.fontSize.xSmall};
    line-height: 1.4;
`);

export const BogIndicatorSelectionList = styled(RaceIndicator, { label: 'BogIndicatorSelectionList' })`
    margin-right: 4px;
`;

interface BetSlipSelectionWrapperPropType {
    manyBets?: boolean;
    isHidden?: boolean;
}

export const BetSlipSelectionWrapper = styled('li', { label: 'BetSlipSelectionWrapper' })<BetSlipSelectionWrapperPropType>`
    margin: 16px 0;
    position: relative;
    display: block;
    transition: 400ms ease-in;
    overflow: hidden;
    ${({ manyBets }): string => manyBets === true ? '' : 'padding-bottom: 0 !important'};
    ${({ isHidden }): string => isHidden === true ? `
        opacity: 0;
        height: 0;
        max-height: 0;
        transition: max-height .2s ease, opacity .2s ease;
        margin: 0;
    ` : `
        opacity: 1;
        max-height: 300px;
        transition: max-height .2s ease, opacity .2s ease;
    `};
`;

export const EventTitle = withConfig(theme => styled('div', { label: 'EventTitle' })`
    display: flex;
    align-items: center;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.medium};
    line-height: 1.3;
    margin-right: 40px;
    padding-bottom: 5px;
    text-align: left;
    @media ${theme.star.mediaQuery.desktop} {
        margin: 0;
    }
`);

export const EventName = withConfig(theme => styled('div', { label: 'EventName' })`
    color: ${theme.star.betslipSelectionList.txtColor};
    align-items: center;
    display: flex;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.medium};
    line-height: 1.43;
    margin: 0 4px 0 0;
    text-align: left;
`);

export const MarketInfoWrapper = withConfig(theme => styled('div', { label: 'MarketInfoWrapper' })`
    color: ${theme.star.betslipSelectionList.txtColorSecondary};
    margin-bottom: 8px;
    padding-right: 24px;
    width: 100%;
`);

export const InfoName = withConfig(theme => styled('div', { label: 'InfoName' })`
    display: inline;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.2;
    position: relative;
`);

export const NameSeparator = withConfig(theme => styled('span', { label: 'NameSeparator' })`
    font-size: ${theme.star.fontSize.xSmall};
    margin: 0 4px;
`);

type SelectionOptionsPropsType = {
    variant: typeof InputVariantType[keyof typeof InputVariantType];
    betslipVariant: DefaultComponentVariantType | null;
}

const setSelectionOptions = (variant: DefaultComponentVariantType| null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            gap: ${theme.star.gap.xSmall};
            height: 36px;
        `;
    }
    return css`
        background-color: ${theme.star.betslipGeneral.bgColorSecondary};
        height: 40px;
    `;
};

export const SelectionOptions = withConfig(theme => styled('div', { label: 'SelectionOptions' })<SelectionOptionsPropsType>`
    ${({ betslipVariant }): SerializedStyles => setSelectionOptions(betslipVariant, theme)}
    align-items: center;
    display: flex;
    font-size: ${theme.star.fontSize.xRegular};
    justify-content: space-between;
    width: 100%;
    border-radius: ${({ variant }): string => variant === InputVariantType.SECONDARY ? '4px': '0'};
    overflow: hidden;
`);

export const EachWayBoxLabel = withConfig(theme => styled('span', { label: 'EachWayBoxLabel' })`
    color: ${theme.star.eachWayBox.txtColor};
    display: block;
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
    position: relative;
    top: 1px;
`);

export const TrashIconWrapper = styled(TrashIcon, { label: 'TrashIconWrapper' })`
    align-self: flex-start;
    cursor: pointer;
    fill: currentcolor;
    flex: 0 0 24px;
    opacity: 0.8;
    padding: 4px;
    width: 24px;
`;

export const RelatedWarningIconWrapper = styled(WarningIcon, { label: 'RelatedWarningIconWrapper' })`
    align-self: center;
    fill: currentcolor;
    margin-right: 5px;
    width: 12px;
`;

export const TitleWrapper = styled('div', { label: 'TitleWrapper' })`
    display: flex;
    justify-content: space-between;
`;


const setEachWayBox = (variant: InputVariantType| null, theme: EmotionTheme): SerializedStyles => {
    if (variant === InputVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.small};
        `;
    }
    return css`
        border-radius: 0;
    `;
};

export const EachWayBox = withConfig(theme => styled('div', { label: 'EachWayBox' })<BetslipEachWayBoxPropsType>`
    ${({ variant }): SerializedStyles => setEachWayBox(variant, theme)}
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.star.eachWayBox.bgColor};
    flex: 0 0 66px;
    height: 100%;
    width: 66px;
`);

export const BonusButton = styled(Button, { label: 'BonusButton' })`
    height: 100%;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    svg {
        fill: currentcolor;
        width: 20px;
    }
`;

export const AppliedFreeBetContainer = withConfig(theme => styled.div<BetslipSelectionPropsType>`
    background-color: ${theme.star.statuses.success.bgColor};
    color: ${theme.star.statuses.success.txtColor};
    padding: 4px 8px;
    margin-top: 8px;

    height: 24px;

    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.regular};

    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'};

    svg {
        fill: currentcolor;
        width: 16px;
    }
`);
